import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  addEdge,
  Panel
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { TableNode } from '../components/TableNode';
import { DSLEditor } from '../components/DSLEditor';
import { TableEditDialog } from '../components/TableEditDialog';
import { CustomOrthogonalEdge } from '../components/CustomOrthogonalEdge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Database, Plus, Download, Save, Home, Undo, Redo, Moon, Sun, FileText, Share2, Copy, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { useDebouncedCallback } from 'use-debounce';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const nodeTypes = {
  tableNode: TableNode,
};

const edgeTypes = {
  custom: CustomOrthogonalEdge,
};

export const CanvasEditorPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [project, setProject] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showTableEditDialog, setShowTableEditDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportedSQL, setExportedSQL] = useState('');
  const [loading, setLoading] = useState(true);
  const [dslWidth, setDslWidth] = useState(450); // Default width for DSL panel
  const [isResizing, setIsResizing] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  
  // Undo/Redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUpdatingFromHistory = useRef(false);

  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  
  // Update nodes with active relationships info whenever edges change
  useEffect(() => {
    const activeColumnIds = new Set();
    
    // Collect all column IDs that have relationships
    edges.forEach(edge => {
      if (edge.data?.source_column_id) {
        activeColumnIds.add(edge.data.source_column_id);
      }
      if (edge.data?.target_column_id) {
        activeColumnIds.add(edge.data.target_column_id);
      }
    });
    
    // Update nodes with active relationship info
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        data: {
          ...node.data,
          activeRelationships: Array.from(activeColumnIds)
        }
      }))
    );
  }, [edges, setNodes]);
  
  // Handle resize drag
  const handleMouseDown = useCallback((e) => {
    setIsResizing(true);
    e.preventDefault();
  }, []);
  
  // Debounced width setter to prevent ResizeObserver loops
  const debouncedSetWidth = useDebouncedCallback((newWidth) => {
    setDslWidth(newWidth);
  }, 50);
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const newWidth = e.clientX;
      // Min width 300px, max width 800px
      if (newWidth >= 300 && newWidth <= 800) {
        debouncedSetWidth(newWidth);
      }
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
    };
    
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isResizing, debouncedSetWidth]);

  // Save to history function - defined early
  const saveToHistory = useCallback((currentNodes, currentEdges) => {
    if (isUpdatingFromHistory.current) return;
    
    const newState = { 
      nodes: JSON.parse(JSON.stringify(currentNodes)), 
      edges: JSON.parse(JSON.stringify(currentEdges)) 
    };
    
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newState);
    
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  // Delete edge handler - uses stable reference
  const handleDeleteEdge = useCallback((edgeId) => {
    setEdges((currentEdges) => currentEdges.filter(e => e.id !== edgeId));
    toast.success('Relasi dihapus');
  }, [setEdges]);

  const loadCanvasState = useCallback((canvasState) => {
    const loadedNodes = canvasState.tables.map(table => ({
      id: table.id,
      type: 'tableNode',
      position: table.position,
      data: {
        id: table.id,
        name: table.name,
        columns: table.columns,
        onColumnClick: handleColumnClick
      }
    }));

    const loadedEdges = canvasState.relationships.map(rel => {
      // Use saved handle positions if available
      let sourceHandle = rel.source_handle;
      let targetHandle = rel.target_handle;
      
      // If no saved handles, calculate optimal ones based on horizontal position
      if (!sourceHandle || !targetHandle) {
        const sourceTable = loadedNodes.find(n => n.id === rel.source_table_id);
        const targetTable = loadedNodes.find(n => n.id === rel.target_table_id);
        
        if (sourceTable && targetTable) {
          const dx = targetTable.position.x - sourceTable.position.x;
          
          if (dx > 0) {
            // Target di kanan
            sourceHandle = `${rel.source_column_id}-right`;
            targetHandle = `${rel.target_column_id}-left`;
          } else {
            // Target di kiri
            sourceHandle = `${rel.source_column_id}-left`;
            targetHandle = `${rel.target_column_id}-right`;
          }
        }
      }
      
      return {
        id: rel.id,
        source: rel.source_table_id,
        target: rel.target_table_id,
        sourceHandle,
        targetHandle,
        type: 'custom',
        animated: true,
        style: { stroke: 'hsl(var(--primary))', strokeWidth: 2 },
        data: {
          ...rel,
          onDelete: handleDeleteEdge
        }
      };
    });

    setNodes(loadedNodes);
    setEdges(loadedEdges);
    
    const initialState = { nodes: loadedNodes, edges: loadedEdges };
    setHistory([initialState]);
    setHistoryIndex(0);
  }, [handleDeleteEdge, setNodes, setEdges]);

  const fetchProject = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data);
      loadCanvasState(response.data.canvas_state);
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [projectId, loadCanvasState, navigate]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  // REMOVED: useEffect that was causing circular re-renders and state loss
  // handleDeleteEdge is now injected directly when edges are created/loaded

  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      isUpdatingFromHistory.current = true;
      const previousState = history[historyIndex - 1];
      setNodes(previousState.nodes);
      setEdges(previousState.edges);
      setHistoryIndex(historyIndex - 1);
      setTimeout(() => { isUpdatingFromHistory.current = false; }, 100);
      toast.success('Undo');
    }
  }, [history, historyIndex, setNodes, setEdges]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isUpdatingFromHistory.current = true;
      const nextState = history[historyIndex + 1];
      setNodes(nextState.nodes);
      setEdges(nextState.edges);
      setHistoryIndex(historyIndex + 1);
      setTimeout(() => { isUpdatingFromHistory.current = false; }, 100);
      toast.success('Redo');
    }
  }, [history, historyIndex, setNodes, setEdges]);

  const handleAddTable = () => {
    const newTableId = uuidv4();
    const newTable = {
      id: newTableId,
      type: 'tableNode',
      position: { x: 100 + nodes.length * 50, y: 100 + nodes.length * 50 },
      data: {
        id: newTableId,
        name: `table_${nodes.length + 1}`,
        columns: [],
        onColumnClick: handleColumnClick
      }
    };
    
    const updatedNodes = [...nodes, newTable];
    setNodes(updatedNodes);
    saveToHistory(updatedNodes, edges);
    toast.success('Table added');
  };

  const handleUpdateTable = (tableId, updates) => {
    const updatedNodes = nodes.map(node => {
      if (node.id === tableId) {
        return {
          ...node,
          data: {
            ...node.data,
            ...updates,
            onColumnClick: handleColumnClick
          }
        };
      }
      return node;
    });
    
    setNodes(updatedNodes);
    saveToHistory(updatedNodes, edges);
    toast.success('Table updated');
  };

  const handleColumnClick = (column) => {
    console.log('Column clicked:', column);
  };

  const handleNodeClick = (event, node) => {
    setSelectedNode(node);
    setShowTableEditDialog(true);
  };

  const handlePaneClick = () => {
    setSelectedNode(null);
  };

  const handleEdgeClick = (event, edge) => {
    console.log('Edge clicked:', edge);
  };

  const onConnect = useCallback(
    (params) => {
      console.log('Creating connection:', params);
      
      // Extract column IDs from handle IDs - format: columnId-direction (left or right)
      const parseHandleId = (handleId) => {
        if (!handleId) return { columnId: '', direction: '' };
        const lastDashIndex = handleId.lastIndexOf('-');
        const columnId = handleId.substring(0, lastDashIndex);
        const direction = handleId.substring(lastDashIndex + 1);
        return { columnId, direction };
      };
      
      const { columnId: sourceColumnId } = parseHandleId(params.sourceHandle);
      const { columnId: targetColumnId } = parseHandleId(params.targetHandle);
      
      const sourceTableId = params.source;
      const targetTableId = params.target;
      
      // Get table objects
      const sourceTable = nodes.find(n => n.id === sourceTableId);
      const targetTable = nodes.find(n => n.id === targetTableId);
      
      if (!sourceTable || !targetTable) {
        console.error('Table not found');
        return;
      }
      
      const sourceColumn = sourceTable.data.columns.find(c => c.id === sourceColumnId);
      const targetColumn = targetTable.data.columns.find(c => c.id === targetColumnId);
      
      if (!sourceColumn || !targetColumn) {
        toast.error('Silakan drag dari kolom ke kolom');
        return;
      }
      
      // AUTO-FLIP LOGIC: Ensure relationship direction is always FK -> PK
      // If user drags from PK to FK, we flip it automatically
      let finalSourceTableId = sourceTableId;
      let finalSourceColumnId = sourceColumnId;
      let finalTargetTableId = targetTableId;
      let finalTargetColumnId = targetColumnId;
      let finalSourceTable = sourceTable;
      let finalTargetTable = targetTable;
      let finalSourceColumn = sourceColumn;
      let finalTargetColumn = targetColumn;
      
      // Check if we need to flip: if source is PK and target is not PK, flip
      const sourceisPK = sourceColumn.is_primary_key || sourceColumn.is_pk || false;
      const targetIsPK = targetColumn.is_primary_key || targetColumn.is_pk || false;
      
      if (sourceisPK && !targetIsPK) {
        // Flip the connection
        console.log('🔄 Auto-flipping: User dragged from PK to FK, correcting direction...');
        finalSourceTableId = targetTableId;
        finalSourceColumnId = targetColumnId;
        finalTargetTableId = sourceTableId;
        finalTargetColumnId = sourceColumnId;
        finalSourceTable = targetTable;
        finalTargetTable = sourceTable;
        finalSourceColumn = targetColumn;
        finalTargetColumn = sourceColumn;
      }
      
      // SMART HORIZONTAL ROUTING
      const calculateOptimalHandles = (srcTable, tgtTable, srcColId, tgtColId) => {
        const srcX = srcTable.position.x;
        const tgtX = tgtTable.position.x;
        
        if (tgtX > srcX) {
          return {
            srcHandle: `${srcColId}-right`,
            tgtHandle: `${tgtColId}-left`
          };
        } else {
          return {
            srcHandle: `${srcColId}-left`,
            tgtHandle: `${tgtColId}-right`
          };
        }
      };
      
      const { srcHandle: finalSourceHandle, tgtHandle: finalTargetHandle } = 
        calculateOptimalHandles(finalSourceTable, finalTargetTable, finalSourceColumnId, finalTargetColumnId);
      
      console.log(`✅ Smart routing: ${finalSourceTable.data.name}.${finalSourceColumn.name} (${finalSourceHandle}) → ${finalTargetTable.data.name}.${finalTargetColumn.name} (${finalTargetHandle})`);
      
      const relationshipData = {
        id: uuidv4(),
        source_table_id: finalSourceTableId,
        source_column_id: finalSourceColumnId,
        target_table_id: finalTargetTableId,
        target_column_id: finalTargetColumnId,
        relationship_type: 'many-to-one',
        on_delete: 'CASCADE',
        on_update: 'CASCADE',
        source_handle: finalSourceHandle,
        target_handle: finalTargetHandle,
        onDelete: handleDeleteEdge
      };
      
      const newEdge = {
        ...params,
        source: finalSourceTableId,
        target: finalTargetTableId,
        sourceHandle: finalSourceHandle,
        targetHandle: finalTargetHandle,
        id: relationshipData.id,
        animated: true,
        style: { stroke: 'hsl(var(--primary))', strokeWidth: 2 },
        type: 'custom',
        data: relationshipData
      };
      
      const updatedEdges = addEdge(newEdge, edges);
      setEdges(updatedEdges);
      
      // Save to history
      setTimeout(() => {
        saveToHistory(nodes, updatedEdges);
      }, 100);
      
      toast.success(`Relasi dibuat: ${finalSourceTable.data.name}.${finalSourceColumn.name} → ${finalTargetTable.data.name}.${finalTargetColumn.name}`);
    },
    [edges, nodes, setEdges, handleDeleteEdge, saveToHistory]
  );

  const handleSaveProject = async () => {
    try {
      const canvasState = {
        tables: nodes.map(node => ({
          id: node.id,
          name: node.data.name,
          columns: node.data.columns,
          position: node.position
        })),
        relationships: edges.map(edge => ({
          ...edge.data,
          // Ensure handle positions are saved
          source_handle: edge.sourceHandle,
          target_handle: edge.targetHandle
        })),
        zoom: reactFlowInstance?.getZoom() || 1,
        position: reactFlowInstance?.getViewport() || { x: 0, y: 0 }
      };

      console.log('Saving canvas state:', canvasState);
      await axios.put(`${API}/projects/${projectId}`, { canvas_state: canvasState });
      toast.success('Project saved successfully');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save project');
    }
  };

  const handleExportSQL = async () => {
    try {
      // Check if there are tables
      if (nodes.length === 0) {
        toast.error('Tidak ada table untuk di-export. Silakan buat table terlebih dahulu.');
        return;
      }
      
      console.log('Exporting SQL...');
      toast.info('Saving project before export...');
      
      const canvasState = {
        tables: nodes.map(node => ({
          id: node.id,
          name: node.data.name,
          columns: node.data.columns,
          position: node.position
        })),
        relationships: edges.map(edge => ({
          ...edge.data,
          // Ensure handle positions are saved
          source_handle: edge.sourceHandle,
          target_handle: edge.targetHandle
        }))
      };

      // Save project first to ensure canvas_state is up to date
      await axios.put(`${API}/projects/${projectId}`, { canvas_state: canvasState });
      console.log('Project saved, now exporting SQL...');
      
      const response = await axios.post(`${API}/projects/${projectId}/export-sql`);
      console.log('SQL Export response:', response.data);
      
      if (!response.data.sql) {
        throw new Error('No SQL returned from server');
      }
      
      setExportedSQL(response.data.sql);
      setShowExportDialog(true);
      toast.success('SQL berhasil di-export!');
    } catch (error) {
      console.error('Export SQL error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error('Failed to export SQL: ' + errorMsg);
    }
  };

  const handleShare = async () => {
    try {
      // Save project first
      await handleSaveProject();
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/projects/${projectId}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const fullShareUrl = `${window.location.origin}/share/${response.data.share_token}`;
      setShareUrl(fullShareUrl);
      setShowShareDialog(true);
      toast.success('Project berhasil di-publish!');
    } catch (error) {
      console.error('Share error:', error);
      toast.error('Failed to share project');
    }
  };

  const handleCopyShareLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Share link copied to clipboard!');
  };

  const handleCopySQL = () => {
    navigator.clipboard.writeText(exportedSQL);
    toast.success('SQL copied to clipboard');
  };

  const handleDownloadSQL = () => {
    const blob = new Blob([exportedSQL], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project?.name || 'schema'}.sql`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('SQL file downloaded');
  };

  const handleDSLUpdate = useCallback((parsed) => {
    console.log('DSL Update:', parsed);
    
    // STEP 1: Build position map from CURRENT nodes (by table name)
    const positionMap = {};
    nodes.forEach(node => {
      positionMap[node.data.name] = node.position;
    });
    
    // STEP 2: Create new nodes, preserving positions for existing tables
    const newNodes = parsed.tables.map(table => {
      const existingPosition = positionMap[table.name];
      
      if (existingPosition) {
        console.log(`✅ Preserving position for ${table.name}`);
        return {
          id: table.id,
          type: 'tableNode',
          position: existingPosition, // USE EXISTING POSITION
          data: {
            id: table.id,
            name: table.name,
            columns: table.columns,
            onColumnClick: handleColumnClick
          }
        };
      } else {
        console.log(`➕ New table: ${table.name} at default position`);
        return {
          id: table.id,
          type: 'tableNode',
          position: table.position, // USE DEFAULT FROM PARSER
          data: {
            id: table.id,
            name: table.name,
            columns: table.columns,
            onColumnClick: handleColumnClick
          }
        };
      }
    });
    
    // STEP 3: Create edges with smart routing
    const newEdges = parsed.relationships.map(rel => {
      let sourceHandle = rel.source_handle;
      let targetHandle = rel.target_handle;
      
      if (!sourceHandle || !targetHandle) {
        const sourceTable = newNodes.find(n => n.id === rel.source_table_id);
        const targetTable = newNodes.find(n => n.id === rel.target_table_id);
        
        if (sourceTable && targetTable) {
          const dx = targetTable.position.x - sourceTable.position.x;
          
          if (dx > 0) {
            sourceHandle = `${rel.source_column_id}-right`;
            targetHandle = `${rel.target_column_id}-left`;
          } else {
            sourceHandle = `${rel.source_column_id}-left`;
            targetHandle = `${rel.target_column_id}-right`;
          }
        }
      }
      
      return {
        id: rel.id,
        source: rel.source_table_id,
        target: rel.target_table_id,
        sourceHandle,
        targetHandle,
        animated: true,
        type: 'custom',
        style: { stroke: 'hsl(var(--primary))', strokeWidth: 2 },
        data: {
          ...rel,
          source_handle: sourceHandle,
          target_handle: targetHandle,
          onDelete: handleDeleteEdge
        }
      };
    });
    
    setNodes(newNodes);
    setEdges(newEdges);
    saveToHistory(newNodes, newEdges);
    
    const preserved = Object.keys(positionMap).length;
    const newTables = parsed.tables.length - preserved;
    if (newTables > 0 || preserved > 0) {
      toast.success(`Posisi ${preserved} table dipertahankan, ${newTables} table baru ditambahkan`);
    }
  }, [nodes, handleColumnClick, handleDeleteEdge, setNodes, setEdges, saveToHistory]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading project...</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      <div className="h-14 border-b border-border bg-background/80 backdrop-blur-md flex items-center justify-between px-4">
        <div className="flex items-center space-x-4">
          <Button
            data-testid="back-to-dashboard-btn"
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
            className="hover:-translate-y-[1px] transition-transform duration-200"
          >
            <Home className="w-5 h-5" strokeWidth={1.5} />
          </Button>
          <div className="flex items-center space-x-2">
            <Database className="w-6 h-6 text-primary" strokeWidth={1.5} />
            <span className="text-lg font-bold" style={{fontFamily: 'Space Grotesk'}}>
              {project?.name}
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            data-testid="undo-btn"
            variant="ghost"
            size="icon"
            onClick={handleUndo}
            disabled={historyIndex <= 0}
            className="hover:-translate-y-[1px] transition-transform duration-200"
            title="Undo (Ctrl+Z)"
          >
            <Undo className="w-4 h-4" strokeWidth={1.5} />
          </Button>
          <Button
            data-testid="redo-btn"
            variant="ghost"
            size="icon"
            onClick={handleRedo}
            disabled={historyIndex >= history.length - 1}
            className="hover:-translate-y-[1px] transition-transform duration-200"
            title="Redo (Ctrl+Y)"
          >
            <Redo className="w-4 h-4" strokeWidth={1.5} />
          </Button>
          <Button
            data-testid="add-table-btn"
            onClick={handleAddTable}
            variant="outline"
            className="hover:-translate-y-[1px] transition-transform duration-200"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Add Table
          </Button>
          <Button
            data-testid="save-project-btn"
            onClick={handleSaveProject}
            className="hover:-translate-y-[1px] transition-transform duration-200"
          >
            <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Save
          </Button>
          <Button
            data-testid="export-sql-btn"
            onClick={handleExportSQL}
            className="hover:-translate-y-[1px] transition-transform duration-200"
          >
            <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Export SQL
          </Button>
          <Button
            onClick={handleShare}
            variant="outline"
            className="hover:-translate-y-[1px] transition-transform duration-200"
          >
            <Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Share
          </Button>

          <Button
            data-testid="theme-toggle-canvas-btn"
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="hover:-translate-y-[1px] transition-transform duration-200"
          >
            {theme === 'light' ? <Moon className="w-5 h-5" strokeWidth={1.5} /> : <Sun className="w-5 h-5" strokeWidth={1.5} />}
          </Button>
        </div>
      </div>

      <div className="flex-1 flex">
        {/* DSL Editor - Resizable Left Panel */}
        <div 
          className="border-r border-border flex-shrink-0 relative"
          style={{ width: `${dslWidth}px` }}
        >
          <DSLEditor
            tables={nodes.map(n => ({ id: n.id, name: n.data.name, columns: n.data.columns, position: n.position }))}
            relationships={edges.map(e => e.data)}
            onUpdate={handleDSLUpdate}
            className="w-full h-full"
          />
          
          {/* Resize Handle */}
          <div
            className={`absolute top-0 right-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/50 transition-colors ${
              isResizing ? 'bg-primary' : ''
            }`}
            onMouseDown={handleMouseDown}
            title="Drag to resize"
          >
            <div className="absolute top-1/2 right-0 -translate-y-1/2 w-1 h-12 bg-primary/30 rounded-l" />
          </div>
        </div>

        {/* Canvas Area - Takes remaining space */}
        <div className="flex-1 relative" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={(changes) => {
              onNodesChange(changes);
              if (!isUpdatingFromHistory.current) {
                const updatedNodes = nodes.map(node => {
                  const change = changes.find(c => c.id === node.id);
                  if (change && change.type === 'position' && change.position) {
                    return { ...node, position: change.position };
                  }
                  return node;
                });
                saveToHistory(updatedNodes, edges);
              }
            }}
            onEdgesChange={(changes) => {
              onEdgesChange(changes);
              // Check if any edge was removed
              const removedEdge = changes.find(c => c.type === 'remove');
              if (removedEdge && !isUpdatingFromHistory.current) {
                const deletedEdge = edges.find(e => e.id === removedEdge.id);
                const updatedEdges = edges.filter(e => e.id !== removedEdge.id);
                saveToHistory(nodes, updatedEdges);
                
                if (deletedEdge?.data) {
                  const sourceTable = nodes.find(n => n.id === deletedEdge.data.source_table_id);
                  const targetTable = nodes.find(n => n.id === deletedEdge.data.target_table_id);
                  const sourceCol = sourceTable?.data.columns.find(c => c.id === deletedEdge.data.source_column_id);
                  const targetCol = targetTable?.data.columns.find(c => c.id === deletedEdge.data.target_column_id);
                  
                  toast.success(`Relasi dihapus: ${sourceTable?.data.name}.${sourceCol?.name} → ${targetTable?.data.name}.${targetCol?.name}`);
                } else {
                  toast.success('Relasi dihapus dari UI flow & DSL');
                }
              }
            }}
            onConnect={onConnect}
            onNodeClick={handleNodeClick}
            onEdgeClick={handleEdgeClick}
            onPaneClick={handlePaneClick}
            onInit={setReactFlowInstance}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            connectionMode="loose"
            fitView
            snapToGrid
            snapGrid={[15, 15]}
            deleteKeyCode="Delete"
            multiSelectionKeyCode="Control"
            defaultEdgeOptions={{
              animated: true,
              style: { stroke: 'hsl(var(--primary))' }
            }}
          >
            <Background 
              variant={BackgroundVariant.Dots} 
              gap={20} 
              size={1}
              color="hsl(var(--border))"
            />
            <Controls 
              className="bg-card border border-border rounded-md shadow-lg"
              showInteractive={false}
            />
            <MiniMap 
              className="bg-card border border-border rounded-md"
              nodeColor="hsl(var(--primary))"
              maskColor="hsl(var(--accent))"
            />
            
            {/* Delete hint overlay */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-card/90 backdrop-blur-sm border border-border rounded-lg px-4 py-2 text-xs text-muted-foreground pointer-events-none shadow-lg">
              <div className="flex items-center space-x-4">
                <span>💡 <strong>Tip:</strong></span>
                <span>Klik edge/relasi lalu tekan <kbd className="px-2 py-0.5 bg-accent rounded border border-border font-mono">Delete</kbd> untuk hapus</span>
              </div>
            </div>
          </ReactFlow>
        </div>
      </div>

      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh]" data-testid="export-sql-dialog">
          <DialogHeader>
            <DialogTitle style={{fontFamily: 'Space Grotesk'}}>Export MySQL SQL</DialogTitle>
            <DialogDescription>
              Copy or download your database schema as SQL
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <pre
              data-testid="sql-output"
              className="p-4 bg-accent/30 border border-border rounded-md text-xs overflow-auto max-h-96"
              style={{fontFamily: 'JetBrains Mono'}}
            >
              {exportedSQL}
            </pre>
          </div>
          <DialogFooter>
            <Button
              data-testid="copy-sql-btn"
              variant="ghost"
              onClick={handleCopySQL}
            >
              Copy to Clipboard
            </Button>
            <Button
              data-testid="download-sql-btn"
              onClick={handleDownloadSQL}
              className="hover:-translate-y-[1px] transition-transform duration-200"
            >
              <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Download SQL
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Share2 className="w-5 h-5" />
              <span>Share Project</span>
            </DialogTitle>
            <DialogDescription>
              Anyone with this link can view your project and export SQL (read-only)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center space-x-2 p-3 bg-accent/30 rounded-lg border border-border">
              <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <code className="flex-1 text-xs font-mono truncate">{shareUrl}</code>
              <Button size="sm" variant="outline" onClick={handleCopyShareLink}>
                <Copy className="w-3 h-3 mr-2" />
                Copy
              </Button>
            </div>
            <div className="text-sm text-muted-foreground space-y-2">
              <p>🔓 <strong>Public access:</strong> Anyone with the link can view this project</p>
              <p>👁️ <strong>View-only:</strong> Viewers cannot edit or modify the schema</p>
              <p>📥 <strong>Export enabled:</strong> Viewers can export SQL</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <TableEditDialog
        open={showTableEditDialog}
        onOpenChange={setShowTableEditDialog}
        table={selectedNode}
        onSave={handleUpdateTable}
      />
    </div>
  );
};
